import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useGraphQLClient } from "@package/graphql/react";
import { monthCodeToOffset } from "@package/grid-utils";
import { z } from "zod";

import type {
  GlobalProductInfoFragmentFragment,
  ProductInfoFragmentFragment,
  GlobalProductsByProductsIdsQuery,
  GlobalPackagesByProductIdsQuery,
  ProductUserSettingsFragmentFragment,
} from "../../__generated__/gql/graphql";

import {
  AllSourcesForProductsQuery,
  GlobalPackagesByProductIds,
  GlobalProductInfoFragment,
  GlobalProductsByProductsIds,
  GlobalProductsForLiveCharts,
  GridProductsByIds,
  PackagesDpQuery,
  ProductInfoFragment,
  ProductInfoQuery,
  ProductUserSettingsByUserIdQuery,
  ProductUserSettingsFragment,
  UpsertProductUserSettingsMutation,
} from "./queries";
import { useProductConfigsCache } from "../cached/preload";
import { readFragment } from "../../graphql";
import { createEntityQueryHook } from "../../utils/query";
import { useUserId } from "../../context/auth/hooks";

export type TProduct = ProductInfoFragmentFragment;
export type TGlobalProduct = GlobalProductInfoFragmentFragment;

export const statusMapSchema = z.record(z.string(), z.string()).optional();
export const incrementMapSchema = z.record(z.string(), z.number()).optional();

export type TStatusMap = z.infer<typeof statusMapSchema>;
export type TIncrementMap = z.infer<typeof incrementMapSchema>;

// PRODUCT INFO
export const useProductsInfoQueryOptions = () => {
  const cache = useProductConfigsCache();
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return queryOptions({
    queryKey: ["products", userId],
    queryFn: async () => {
      const info = await cache.load(graphql, userId);
      return info.info ?? Promise.reject("Missing product info in idb");
    },
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 30000),
    retry: (failureCount) => {
      if (failureCount > 20) return false;
      return true;
    },
  });
};
export const useAllProducts = () => useQuery(useProductsInfoQueryOptions());

export const useAllProductsFresh = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useQuery({
    queryKey: ["products", "fresh"],
    queryFn: async () => {
      const res = await graphql.execute(ProductInfoQuery, { folioUser: userId });
      return readFragment(ProductInfoFragment, res.data?.product);
    },
  });
};

export const useProductById = (id?: string) =>
  useQuery({
    ...useProductsInfoQueryOptions(),
    enabled: !!id,
    select: (data) => (id ? data?.[id] : null),
  });

/** One product from the API, not IndexedDB (`useProductById`). Use when source/package metadata must be fresh (IDB can lag; reload/deploy don’t clear it). Costs one `GridProductsByIds` call per id. */
export const useProductByIdFresh = (id?: string) => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useQuery({
    queryKey: ["products", "fresh-by-id", userId, id],
    enabled: !!id && !!userId,
    queryFn: async () => {
      const res = await graphql.execute(GridProductsByIds, { ids: [id!], folioUser: userId });
      const frags = readFragment(ProductInfoFragment, res.data?.product) ?? [];
      return frags[0] ?? null;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

export const useProductsByIds = (ids: string[]) =>
  useQuery({
    ...useProductsInfoQueryOptions(),
    select: (data) => ids.map((id) => data?.[id]).filter(Boolean),
  });

export const useProductName = (id: string) =>
  useQuery({
    ...useProductsInfoQueryOptions(),
    select: (data) => data?.[id]?.name,
  });

// PRODUCT CONFIGS
export const useProductsConfigQueryOptions = () => {
  const cache = useProductConfigsCache();
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return queryOptions({
    queryKey: ["configs", userId],
    queryFn: async () => {
      const info = await cache.load(graphql, userId);
      return info.configs ?? Promise.reject("Missing product configs in idb");
    },
  });
};

export const useProductConfigs = () => useQuery(useProductsConfigQueryOptions());
export const useProductConfigById = (id: string) =>
  useQuery({ ...useProductsConfigQueryOptions(), select: (data) => data?.[id] });

// GLOBAL PRODUCTS
export const useGlobalProducts = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useQuery({
    queryKey: ["global_product", "by-user", userId],
    queryFn: async () => {
      const res = await graphql.execute(GlobalProductsForLiveCharts, { folioUser: userId });
      return readFragment(GlobalProductInfoFragment, res.data?.global_product);
    },
  });
};

export function useGlobalPackagesByProductIds(productIds: string[]) {
  const graphql = useGraphQLClient();
  return useQuery({
    queryKey: ["global_package", productIds],
    enabled: !!productIds.length,
    queryFn: async () => {
      const res = await graphql.execute(GlobalPackagesByProductIds, {
        productIds: productIds,
      });
      return res.data?.global_package;
    },
  });
}

export function useGlobalProductsByProductIds(productIds: string[]) {
  const graphql = useGraphQLClient();
  return useQuery({
    queryKey: ["global_product", productIds],
    queryFn: async () => {
      const res = await graphql.execute(GlobalProductsByProductsIds, {
        productIds: productIds,
      });
      return res.data?.global_product;
    },
  });
}

export type TGlobalPackagesByProductIds = GlobalPackagesByProductIdsQuery["global_package"];
export type TGlobalProductsByProductIds = GlobalProductsByProductsIdsQuery["global_product"];

export const useProductGlobalInstruments = (productId: string, month: string, enabled: boolean = true) => {
  const product = useProductById(productId);
  const graphql = useGraphQLClient();
  const ids = product.data?.product_global_dependencies?.map((d) => d.global_product);
  return useQuery({
    queryKey: ["productGlobalSources", productId, month],
    queryFn: () =>
      graphql
        .execute(AllSourcesForProductsQuery, { products: ids ?? [], month: month })
        .then((res) => res.data?.global_instrument ?? []),
    enabled: enabled && !!ids?.length && !!month,
  });
};

export const useIsEditable = (productId: string, month: string) => {
  const product = useProductById(productId);
  const productConfigs = useProductConfigs();
  const offset = monthCodeToOffset(month);
  if (!product?.data?.has_shared_cell) return false;
  const formula = productConfigs.data?.[productId]?.find((config) => config.offset === offset);
  return !formula || formula.formula === null;
};

export const usePackagesDp = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  return useQuery({
    queryKey: ["packages", "dp", userId],
    queryFn: async () => {
      const res = await graphql.execute(PackagesDpQuery);
      const packages = res.data?.package;
      return new Map((packages ?? []).map((p) => [p.id, p.dp])) as Map<number, number>;
    },
  });
};

export type TProductUserSettings = ProductUserSettingsFragmentFragment;
export const useProductUserSettingsCache = createEntityQueryHook<Map<string, TProductUserSettings>>(() => [
  "productUserSettings",
  useUserId(),
]);

export const useProductUserSettings = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();
  const cache = useProductUserSettingsCache();
  return useQuery({
    queryKey: cache.queryKey,
    queryFn: async () => {
      const res = await graphql.execute(ProductUserSettingsByUserIdQuery, { userId });
      const settings = readFragment(ProductUserSettingsFragment, res.data?.product_user_settings);
      return new Map((settings ?? []).map((s) => [s.product_id, s]));
    },
  });
};

export const useUpsertProductUserSettings = () => {
  const userId = useUserId();
  const graphql = useGraphQLClient();
  const cache = useProductUserSettingsCache();
  return useMutation({
    mutationFn: ({ productId, dp }: { productId: string; dp: number | null }) =>
      graphql.execute(UpsertProductUserSettingsMutation, {
        userId,
        productId,
        dp,
      }),
    onMutate: ({ productId, dp }) => {
      cache.set((old) => {
        const next = new Map(old);
        const existing = next.get(productId);
        next.set(productId, existing ? { ...existing, dp } : { user_id: userId, product_id: productId, dp });
        return next;
      });
    },
    onSettled: () => {
      cache.invalidate();
    },
  });
};
